import normalize from 'json-api-normalizer';
import { createAsyncThunk, createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit';
import {
  BookNowEntry,
  Conversation,
  JobBids,
  JobBidsStatusBuyer,
  JobPriceDefiningAnswers,
  JobPriceDefiningQuestions,
  JSONApiDataObject,
  JSONApiDataObjectLoadable,
  MatchedProInfo,
  MilestonesData,
  ReduxEntity,
  UserV2
} from 'Douleutaras/types';
import { RootState } from 'Douleutaras/redux/store';
import { makeRequest } from 'Douleutaras/services/apiClient';
import { usersSlice } from 'Douleutaras/redux/data/users/usersSlice';
import { jobBidsFeedbackSlice } from 'Douleutaras/redux/data/jobBids/jobBidsFeedbackSlice';
import { jobBidsBookNowSlice } from 'Douleutaras/redux/data/jobBids/jobBidsBookNowSlice';
import { conversationsSlice } from 'Douleutaras/redux/data/conversations/conversationsSlice';
import { jobBidsStatusBuyerSlice } from 'Douleutaras/redux/data/jobBids/jobBidsStatusBuyerSlice';
import { matchedProInfoSlice } from 'Douleutaras/redux/data/jobBids/matchedProInfoSlice';
import jobPriceDefiningAnswersSlice from 'Douleutaras/redux/data/jobPriceDefiningAnswers/jobPriceDefiningAnswersSlice';
import { API_JOB_ENDPOINT, jobsSlice } from 'Douleutaras/redux/data/jobs/jobsSlice';
import { infinitePaginationMergeMeta } from 'Douleutaras/utilities/redux/merge';
import { mapTabAttribute } from 'Douleutaras/views/seller-dashboard/jobs-list/constants/jobTabsConfiguration';
import { jobMunicipalitySlice } from 'Douleutaras/redux/data/jobMunicipality/jobMunicipalitySlice';
import { categoriesSlice } from 'Douleutaras/redux/data/categories/categoriesSlice';
import jobPriceDefiningQuestionsSlice from 'Douleutaras/redux/data/jobPriceDefiningQuestions/jobPriceDefiningQuestionsSlice';
import { getCrmOptions } from 'Douleutaras/redux/data/v2/crm/crmSlice';
import { fetchCounters } from 'Douleutaras/redux/data/v2/counters/countersSlice';
import { Gis } from 'Douleutaras/components/Forms/gis.class';
import { BidListStatuses, TabIds } from 'Douleutaras/views/seller-dashboard/jobs-list/types';
import { fetchBidsWithOuter } from 'Douleutaras/redux/data/jobBidsWithOuter/jobBidsWithOuter';
import { ProPaymentRequest, proRequestsAdded } from 'Douleutaras/redux/data/payments/proPaymentRequestSlice';
import {
  CustomerOnlinePayment,
  customerOnlinePaymentAdded
} from 'Douleutaras/redux/data/payments/customerOnlinePaymentSlice';
import { API_BID_ENDPOINT } from 'Douleutaras/redux/data/jobBids/constants';
import { Dictionary } from 'lodash';

export const BidFetchEndpoint = jobId => `jobBid/${jobId}`;

interface JobBidsNormalizedEntityList {
  users: { [index in number]: JSONApiDataObject<UserV2> };
  bids: { [index in number]: JSONApiDataObject<JobBids> };
  bidFeedbacks: { [index in number]: JSONApiDataObject<unknown> };
  bidStatusBuyerUpdate: { [index in number]: JSONApiDataObject<JobBidsStatusBuyer> };
  bookNowEntries: { [index in number]: JSONApiDataObject<BookNowEntry> };
  conversations: { [index in number]: JSONApiDataObject<Conversation> };
  answers: { [index in number]: JSONApiDataObject<JobPriceDefiningAnswers> };
  questions: { [index in number]: JSONApiDataObject<JobPriceDefiningQuestions> };
  proPaymentRequests: { [index in number]: JSONApiDataObject<ProPaymentRequest> };
  customerOnlinePayments: { [index in number]: JSONApiDataObject<CustomerOnlinePayment> };
  matchedProInfo: { [index in number]: JSONApiDataObject<MatchedProInfo> };
  meta: {};
}

interface UpdateBidResult {
  payload: {
    status: number;
  };
  meta: {
    requestId: string;
    requestStatus: 'fulfilled' | 'rejected' | 'pending';
    arg: {
      bidId: string;
      // tslint:disable-next-line:no-any
      attributes: Dictionary<any>;
    };
  };
}

const jobBidsAdapter = createEntityAdapter<JSONApiDataObjectLoadable<JobBids>>({
  selectId: item => item.id
});

export const fetchJobBids = createAsyncThunk(
  'jobBids/getAll',
  async ({ jobId, includes }: { jobId: string, includes?: Array<string> } = undefined, api) => {
    const endpoint = `${API_JOB_ENDPOINT}/${jobId}/bids/`;
    const response = await makeRequest(
      endpoint,
      { includes }
    )(api);

    if (response.status === 200 || response.status === 201) {
      return jobBidsHandleResponse(response, api, BidFetchEndpoint(jobId));
    }

    return api.rejectWithValue({ error: response.payload });
});

// Transforms combination of Status into meaningful params to get from the API,
// that's because we don't have a single way to get each status properly, and we need combination of params.
const mapTabToBidEndpointParams = (status: BidListStatuses, tabSelection?: TabIds) => {
  switch (true) {
    default:
    case status === BidListStatuses.CURRENT:
      return {
        sort: '-book_now_entry--modified',
        'filter[tab]': [TabIds.NEW, TabIds.TODAY, TabIds.NEXT, TabIds.LATER].indexOf(tabSelection) !== -1
          ? tabSelection
          : TabIds.ALL
      };
    case status === BidListStatuses.CONCLUDED && tabSelection === TabIds.BY_YOU:
      return {
        'filter[tab]': 'concluded',
        'filter[concluded_by_pro]': '1',
        sort: '-book_now_entry--modified'
      };
    case status === BidListStatuses.CONCLUDED && tabSelection === TabIds.BY_ANOTHER_PROFESSIONAL:
      return {
        'filter[tab]': 'concluded_by_another_pro',
        sort: '-book_now_entry--modified'
      };
    case status === BidListStatuses.CONCLUDED && tabSelection === TabIds.OUTSIDE_DOULEUTARAS:
      return {
        'filter[tab]': 'concluded',
        'filter[job__cancel_reason]': 'seller_not_douleutaras',
        sort: '-book_now_entry--modified'
      };
    case status === BidListStatuses.CONCLUDED:
      return {
        'filter[tab]': 'concluded',
        sort: '-book_now_entry--modified'
      };
    case status === BidListStatuses.CANCELLED && tabSelection === TabIds.BY_YOU:
      return {
        'filter[tab]': 'cancelled',
        'filter[cancelled_by_pro]': '1'
      };
    case status === BidListStatuses.CANCELLED && tabSelection === TabIds.BY_THE_CUSTOMER:
      return {
        'filter[tab]': 'cancelled',
        'filter[cancelled_by_pro]': '0'
      };
    case status === BidListStatuses.CANCELLED:
      return {
        'filter[tab]': 'cancelled'
      };
  }
};

function createEndpointIdentifier(status: BidListStatuses, tabSelection: TabIds): string {
  return `${API_BID_ENDPOINT}${status}/${tabSelection}`;
}

interface FetchBidsArgs {
  status: BidListStatuses;
  page?: number;
  params?: {};
  filterJobStatuses?: Array<string>;
  filterJobIntent?: Array<string>;
  filterJobCategory?: Array<string>;
  tabSelection: TabIds;
}
export const fetchBidsWithCrmOptions = createAsyncThunk(
  'bids/get/crm',
  async ({ bidParams, withCounters }: { bidParams: FetchBidsArgs, withCounters: boolean }, api) => {
    // Fetch Tab counters
    if (withCounters) {
      api.dispatch(fetchCounters({ tab: bidParams.status }));
    }
    const res = await api.dispatch(fetchBidsWithOuter(bidParams));

    if (res.meta.requestStatus === 'fulfilled' && res.payload?.bids) {
      // tslint:disable-next-line:variable-name
      const bid_ids = Object.keys(res.payload.bids)
        .join(',');
      // Fetch CRM Options for given bids
      api.dispatch(getCrmOptions({ bid_ids }));
    }
  }
);

export const fetchBidsV3 = createAsyncThunk(
  'bids/get',
  async ({ status, page, params = {}, filterJobStatuses = [], filterJobIntent, filterJobCategory, tabSelection }
           : FetchBidsArgs = undefined,
         api) => {
    const endpointIdentifier = createEndpointIdentifier(status, tabSelection);
    const bidEndpointParams = mapTabToBidEndpointParams(status, tabSelection);
    // Filters
    const jobIntentFilter = { 'filter[intention]': filterJobIntent };
    const jobCategoryFilter = { 'filter[job__category]': filterJobCategory };
    const jobStatusFilter = filterJobStatuses.length > 0 ? {
        'filter[book_now_entry__status]': filterJobStatuses.join(',')
      } : {};

    const response = await makeRequest(
      API_BID_ENDPOINT,
      {
        method: 'GET',
        additionalGetParams: {
          ...{
            'page[number]': page
          },
          ...params,
          ...bidEndpointParams,
          ...jobStatusFilter,
          ...jobCategoryFilter,
          ...jobIntentFilter
        }
      }
    )(api);

    return bidHandleResponse(response, api, endpointIdentifier);
  });

export const fetchBidsOptions = createAsyncThunk(
  'bids/options',
  async (args, api) => {

    const response = await makeRequest(
      API_BID_ENDPOINT,
      {
        method: 'OPTIONS'
      }
    )(api);

    return response.data;
  });

export const fetchBid = createAsyncThunk(
  'bid/get',
  async ({ bidId, includes, params }: { bidId: string, includes?: Array<string>, params?: {} }, api) => {
    const endpoint = `${API_BID_ENDPOINT}${bidId}/`;
    const response = await makeRequest(
      endpoint,
      { includes, additionalGetParams: { ...params } }
    )(api);

    if (response.status === 200 || response.status === 201) {
      return bidHandleResponse(response, api, `bid/${bidId}`);
    }

    return api.rejectWithValue({ error: response.payload });
  });

export const fetchPaymentFieldsBid = createAsyncThunk(
  'bid/paymentFields/get',
  async ({ bidId }: { bidId: string }, api) => {
    const endpoint = `${API_BID_ENDPOINT}${bidId}/`;
    const response = await makeRequest(
      endpoint,
      { additionalGetParams: {
          'fields[bids]': [
            'is_eligible_for_customer_online_payment_request',
            'is_eligible_for_online_payment',
            'are_customer_online_payments_enabled',
            'total_accepted_customer_online_payment_amount',
            'total_accepted_customer_online_payment_net_amount',
            'total_pending_customer_online_payment_amount',
            'total_pending_customer_online_payment_net_amount'
          ].join(',')
        }
      }
    )(api);

    return bidHandleResponse(response, api, `bid/paymentFields/${bidId}`).bids[bidId];
  }
);

export const updateBid = createAsyncThunk<UpdateBidResult, UpdateBidResult['meta']['arg']>(
  'bid/patch',
  async ({ bidId, attributes }, api) => {
    api.dispatch(changeBidLoadingState({ id: bidId, loading: true }));

    const response = await makeRequest(
      `${API_BID_ENDPOINT}${bidId}/`,
      {
        method: 'PATCH',
        body: {
          data: {
            type: 'bids',
            id: bidId,
            attributes
          }
        }
      }
    )(api);

    api.dispatch(changeBidLoadingState({ id: bidId, loading: false }));

    return response;
  });

export const updateBidLocation = createAsyncThunk<unknown, { bidId: string; location: Gis }>(
  'bid/location/patch',
  async ({ bidId, location }, api) => {
    const response = await api.dispatch(updateBid({ bidId, attributes: { location } }));

    if (response.payload['status'] === 200 || response.payload['status'] === 201) {
      api.dispatch(storeBidLocation({ id: bidId, data: location }));
    }
  });

export const updateBidVat = createAsyncThunk<unknown, { bidId: string; vat_rate: string }>(
  'bid/vat/patch',
  async ({ bidId, vat_rate }, api) => {
    const response = await api.dispatch(updateBid({ bidId, attributes: { vat_rate } }));

    if (response.payload['status'] === 200 || response.payload['status'] === 201) {
      api.dispatch(storeBidVatRate({ id: bidId, data: vat_rate }));
    }
  });

const jobBidsHandleResponse = (response, api, endpoint) => {
  const jobBidsNormalized = normalize(response.data, { endpoint }) as JobBidsNormalizedEntityList;
  const { usersAdded } = usersSlice.actions;
  const { jobBidsFeedbackAdded } = jobBidsFeedbackSlice.actions;
  const { jobBidsBookNowAdded } = jobBidsBookNowSlice.actions;
  const { conversationsAdded } = conversationsSlice.actions;
  const { bidBuyerStatusAdded } = jobBidsStatusBuyerSlice.actions;
  const { jobPriceDefiningAnswersAdded } = jobPriceDefiningAnswersSlice.actions;
  const { jobPriceDefiningQuestionAdded } = jobPriceDefiningQuestionsSlice.actions;
  const { matchedProInfoAdded } = matchedProInfoSlice.actions;

  if (jobBidsNormalized.users) {
    api.dispatch(
      usersAdded(jobBidsNormalized.users)
    );
  }

  if (jobBidsNormalized.bidFeedbacks) {
    api.dispatch(
      jobBidsFeedbackAdded(jobBidsNormalized.bidFeedbacks)
    );
  }

  if (jobBidsNormalized.conversations) {
    api.dispatch(
      conversationsAdded(jobBidsNormalized.conversations)
    );
  }

  if (jobBidsNormalized.bookNowEntries) {
    api.dispatch(
      jobBidsBookNowAdded(jobBidsNormalized.bookNowEntries)
    );
  }

  if (jobBidsNormalized.bidStatusBuyerUpdate) {
    api.dispatch(
      bidBuyerStatusAdded(jobBidsNormalized.bidStatusBuyerUpdate)
    );
  }

  if (jobBidsNormalized.answers) {
    api.dispatch(
      jobPriceDefiningAnswersAdded(jobBidsNormalized.answers)
    );
  }

  if (jobBidsNormalized.questions) {
    api.dispatch(
      jobPriceDefiningQuestionAdded(jobBidsNormalized.questions)
    );
  }

  if (jobBidsNormalized.proPaymentRequests) {
    api.dispatch(proRequestsAdded(jobBidsNormalized.proPaymentRequests));
  }

  if (jobBidsNormalized.customerOnlinePayments) {
    api.dispatch(customerOnlinePaymentAdded(jobBidsNormalized.customerOnlinePayments));
  }

  if (jobBidsNormalized.matchedProInfo) {
    api.dispatch(matchedProInfoAdded(jobBidsNormalized.matchedProInfo));
  }

  return jobBidsNormalized;
};

export const bidHandleResponse = (response, api, endpoint) => {
  const bidNormalized = normalize(response.data, { endpoint });
  const { jobBidsBookNowAdded } = jobBidsBookNowSlice.actions;
  const { jobsAdded } = jobsSlice.actions;
  const { jobMunicipalityAdded } = jobMunicipalitySlice.actions;
  const { categoriesAdded } = categoriesSlice.actions;
  const { jobPriceDefiningAnswersAdded } = jobPriceDefiningAnswersSlice.actions;
  const { jobPriceDefiningQuestionAdded } = jobPriceDefiningQuestionsSlice.actions;

  if (bidNormalized.bookNowEntries) {
    api.dispatch(jobBidsBookNowAdded(bidNormalized.bookNowEntries));
  }

  if (bidNormalized.jobs) {
    api.dispatch(jobsAdded(bidNormalized.jobs));
  }

  if (bidNormalized.municipality) {
    api.dispatch(jobMunicipalityAdded(bidNormalized.municipality));
  }

  if (bidNormalized.categories) {
    api.dispatch(categoriesAdded(bidNormalized.categories));
  }

  if (bidNormalized.answers) {
    api.dispatch(jobPriceDefiningAnswersAdded(bidNormalized.answers));
  }

  if (bidNormalized.questions) {
    api.dispatch(jobPriceDefiningQuestionAdded(bidNormalized.questions));
  }

  if (bidNormalized.proPaymentRequests) {
    api.dispatch(proRequestsAdded(bidNormalized.proPaymentRequests));
  }

  if (bidNormalized.customerOnlinePayments) {
    api.dispatch(customerOnlinePaymentAdded(bidNormalized.customerOnlinePayments));
  }

  bidNormalized.endpoint = endpoint;

  return bidNormalized;
};

interface JobBidsReduxEntity<T, A> extends ReduxEntity<T, A> {
  milestones: Array<Object>;
  options: Partial<{
    name: string;
    description: string;
    renders: Array<string>,
    parses: Array<string>,
    actions: {
      [s: string]: {
        [s: string]: {
          type: string;
          required: boolean;
          readonly: boolean;
          label: string;
        };
      }
    },
    crm_status: Array<Array<string>>
    categories: Array<{
      id: string,
      title: string
    }>
  }>;
}

export const jobBidsSlice = createSlice({
  name: 'jobBids',
  initialState: jobBidsAdapter.getInitialState<JobBidsReduxEntity<JobBids, MilestonesData>>({
    loading: false,
    error: false,
    meta: {
      pagination: {
        count: undefined,
        page: undefined,
        pages: undefined
      },
      meta: {},
      data: []
    },
    entities: {},
    milestones: [],
    options: {}
  }),
  reducers: {
    jobBidsAdded: (state, action) => {
      jobBidsAdapter.setMany(state, action.payload);
    },
    jobBidsAddBidsBuyerStatusRelation: (state, action) => {
      state.entities[action.payload.bidId].relationships = {
        ...state.entities[action.payload.bidId].relationships,
        updateFromBuyer: {
          // @ts-ignore
          data: {
            id: action.payload.id,
            type: 'bidStatusBuyerUpdate'
          }
        }
      };
    },
    jobBidAddJobDefiningAnswersRelation: (state, action) => {
      state.entities[action.payload.bidId].relationships = {
        ...state.entities[action.payload.bidId].relationships,
        priceDefiningAnswers: { data: action.payload.data }
      };
    },
    jobBidAddProPaymentRequestRelation: (state, action) => {
      state.entities[action.payload.bidId].relationships = {
        ...state.entities[action.payload.bidId].relationships,
        proPaymentRequest: { data: action.payload.data }
      };
    },
    jobBidRemoveProPaymentRequestRelation: (state, action) => {
      state.entities[action.payload.bidId].relationships = {
        ...state.entities[action.payload.bidId].relationships,
        proPaymentRequest: { data: undefined }
      };
    },
    jobBidAddCustomerOnlinePaymentRelation: (state, action) => {
      state.entities[action.payload.bidId].relationships = {
        ...state.entities[action.payload.bidId].relationships,
        customerOnlinePayment: { data: action.payload.data }
      };
    },
    jobBidRemoveCustomerOnlinePaymentRelation: (state, action) => {
      state.entities[action.payload.bidId].relationships = {
        ...state.entities[action.payload.bidId].relationships,
        customerOnlinePayment: { data: undefined }
      };
    },
    changeBidLoadingState: (state, action) => {
      state.entities[action.payload.id].loading = action.payload.loading;
    },
    storeBidLocation: (state, action) => {
      state.entities[action.payload.id].attributes.location = action.payload.data;
    },
    storeBidVatRate: (state, action) => {
      state.entities[action.payload.id].attributes.vatRate = action.payload.data;
    }
  },
  extraReducers: builder => {
    builder.addCase(fetchJobBids.fulfilled, (state, action) => {
      if (action.payload.bids) {
        jobBidsAdapter.setMany(state, action.payload.bids);
        state.meta = { ...state.meta, ...action.payload.meta };
      }
      state.loading = false;
      state.error = false;
    });
    builder.addCase(fetchJobBids.rejected, (state, action) => {
      state.loading = false;
      state.error = true;
    });

    builder.addCase(fetchJobBids.pending, (state, action) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(fetchBid.fulfilled, (state, action) => {
      if (action.payload.bids) {
        jobBidsAdapter.setMany(state, action.payload.bids);
      }
      state.loading = false;
      state.error = false;
    });
    builder.addCase(fetchBid.rejected, (state, action) => {
      state.loading = false;
      state.error = true;
    });
    builder.addCase(fetchPaymentFieldsBid.fulfilled, (state, action) => {
      if (action.payload) {
        jobBidsAdapter.updateMany(state, [
          {
            id: action.payload.id,
            changes: { attributes: { ...state.entities[action.payload.id].attributes, ...action.payload.attributes }}
          }
        ]);
      }
    });
    builder.addCase(fetchBidsV3.pending, (state, action) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(fetchBidsV3.fulfilled, (state, action) => {
      if (action.payload.bids) {
        jobBidsAdapter.setMany(state, action.payload.bids);
      }

      infinitePaginationMergeMeta(action, state);

      state.loading = false;
      state.error = false;
    });
    builder.addCase(fetchBidsOptions.fulfilled, (state, action) => {
      state.options = action.payload.data;
    });
  }
});

// Select bids list based on tab and page number
export const selectBidsMetaByTab = createSelector([
  state => state.jobBids,
  (state, { tab, status }) => ({ tab, status })
], (bids, { tab, status }) => {

  const metaKey = `${API_BID_ENDPOINT}${status}/${tab}`;
  // The current tab doesn't need different key, it loads one list
  // Logic of tabs goes here...
  const metaBids = bids.meta[metaKey]?.data;

  // Return all bids only on switching tabs which are not in "Current" status
  // Current status works in a different way, they don't fetch new bids but use existing instead
  if (tab === TabIds.ALL) {

    return metaBids;
  }

  return metaBids?.filter(metaBid => {
    const bid = bids.entities[metaBid.id];

    return mapTabAttribute(bid.attributes.tab) === tab;
  });
});

export const {
  changeBidLoadingState,
  storeBidLocation,
  storeBidVatRate,
  jobBidAddProPaymentRequestRelation,
  jobBidRemoveProPaymentRequestRelation,
  jobBidAddCustomerOnlinePaymentRelation,
  jobBidRemoveCustomerOnlinePaymentRelation
} = jobBidsSlice.actions;
export const jobBidsSelectors = jobBidsAdapter.getSelectors((state: RootState) => state.jobBids);
